import { AlertFactory, AlertState } from 'alerts/Alert'
import { GetCurrentMember } from 'iam/SessionStore'
import ModalViewModel from 'infra/ModalViewModel'
import Job, { Collaborator } from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { maxLength } from 'utils/validation-utils'
import { required, validationContext } from 'validx'

/**
 * View Model for the Remove Collaborators as Admin dialog.
 * @extends ModalViewModel
 */
export default class RemoveCollaboratorsAsAdminViewModel extends ModalViewModel {
  /**
   * Search text.
   */
  @observable searchText = ''

  /**
   * Collaborator to remove.
   */
  @observable collaborator?: Collaborator = undefined

  /**
   * Disables submit button.
   */
  @observable disabled = true

  /**
   * The reason for removing the collaborator.
   */
  @observable reason = ''

  /**
   * Hold the maximum length of reason field.
   */
  private readonly reasonLength = {
    reason: { max: 100 },
  }

  /**
   * Helper text for reason input field.
   */
  reasonHelpText = `Why are you removing the collaborator?
    This text is public and can be seen by both Client and Pros.`

  /**
   * Validation context for View Model.
   */
  validation = validationContext(this, {
    collaborator: [required('Collaborator is required.')],
    reason: [
      maxLength(
        this.reasonLength.reason.max,
        `Reason must be less than ${this.reasonLength.reason.max} characters.`,
      ),
    ],
  })

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private newAlert: AlertFactory,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Supporting Collaborators on the Job.
   */
  @computed
  get supportingCollaborators() {
    if (this.canOnlyRemoveClients) {
      return this.job.supportingClients
    }

    return this.job.supportingPros.concat(this.job.supportingClients)
  }

  /**
   * Collaborator for current user.
   */
  @computed
  get currentUserCollaborator() {
    return this.job.collaborators.find((c) => c.member.isCurrentUser)
  }

  /**
   * Filtered collaborators.
   */
  @computed
  get filteredCollaborators() {
    const lowerSearchText = this.searchText.toLowerCase()

    return this.supportingCollaborators.filter(
      (supportingCollaborator: Collaborator) => {
        const shouldBeRemoved = this.job.collaborators.some(
          (c) =>
            c.member === supportingCollaborator.member &&
            c.role === 'SUPPORTING',
        )

        return (
          shouldBeRemoved &&
          supportingCollaborator.member.displayName
            .toLowerCase()
            .includes(lowerSearchText)
        )
      },
    )
  }

  /**
   * Indicates if the user can only remove clients.
   */
  @computed
  get canOnlyRemoveClients() {
    return (
      !this.getCurrentMember(this.job.currentMemberWorkspaceId).hasPermission(
        'ADMIN_USER_LIST',
      ) &&
      this.getCurrentMember(this.job.currentMemberWorkspaceId).hasPermission(
        'ADMIN_CLIENT_USER_LIST',
      )
    )
  }

  /**
   * Sets the search text.
   */
  @action.bound
  setSearchText(newText: string) {
    this.searchText = newText
  }

  /**
   * Sets a collaborator to remove.
   */
  @action.bound
  setCollaborator(collaborator?: Collaborator) {
    if (this.collaborator === collaborator) {
      return
    }

    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.reason = ''
    this.disabled = true

    this.removeCollaboratorAsAdmin.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Removes the collaborator as Admin.
   */
  removeCollaboratorAsAdmin = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const collaborator = this.job.collaborators.find(
      (c) => c.member === this.collaborator?.member,
    )

    if (collaborator) {
      const alert = this.newAlert()
        .withState(AlertState.Pending)
        .saying(
          'Removing Collaborator',
          `Removing ${collaborator.member.displayName} from ${this.job.name}...`,
        )
        .show()

      await this.jobStore.removeCollaboratorAsAdmin(
        Id.assert(this.job.id),
        Id.assert(collaborator.member.user.publicId),
        this.reason,
      )

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${collaborator.member.displayName} was removed from ${this.job.name}.`,
        )
        .autoDismiss()
    }

    this.close()
  })

  /**
   * Shows Removes Collaborators as Admin Dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }
}
